import { log } from "@jidlomat/terminal-core/utils/logger";
import * as Sentry from "@sentry/react";
import type { FlowKind } from "./loadFlow";

/** What an import was rejected with, in words. String() throws on an object without a prototype. */
const inWords = (rejection: unknown): string => {
	if (rejection instanceof Error) {
		return rejection.message;
	}
	try {
		return String(rejection);
	} catch {
		return "a value that cannot be put into words";
	}
};

/**
 * Tells Sentry that the chunk of a flow could not be fetched. Every tablet fetches its own chunk, so
 * every tablet reports, as with every other exception of the app.
 *
 * It is one error that names what the import was rejected with, not an error with a `cause`. A
 * chain reaches Sentry as two exceptions with the cause first, and the normalizer of the app
 * (services/external/sentry.ts) picks the fingerprint by the first: the grouping would hang on the
 * wording of the engine, which in Chromium names the chunk with the hash of the build and in a
 * failed fetch of WebKit is the "Load failed" of every network error. The rule "flow-chunk-failed"
 * there keeps the events one issue under this name.
 */
export const reportFlowFailure = (kind: FlowKind, rejection: unknown) => {
	const message = `Flow chunk failed to load (${kind})`;
	// For whoever has the console open. Sentry does not capture warnings from the console, so this
	// is no second event.
	log(message, "warning", rejection);
	Sentry.captureException(new Error(`${message}: ${inWords(rejection)}`));
};
